import React, { useState, useEffect } from 'react';
import {
  Grid,
  makeStyles,
  TableRow,
  Table,
  Theme,
  Typography
} from '@material-ui/core';
import { BoxWrapper } from 'components/Base';
import Fields from 'components/Fields';
import TableComponents from 'components/Table';
import {
  sumRecommendedWeighting,
  sumExistingWeighting,
  formatPercent,
  Notify,
  formatCurrency,
  errHelper
} from 'utils';
import { FundType } from 'store/modules/Funds/types';
import Fund from './Fund';
import CipTable from './CipTable';
import AssetAllocations from '../AssetAllocations';
import api from 'api';
import { ModelPortfolioType } from 'store/modules/Meta/types';
import CipSelection from './CipSelection';
import BulkImport from 'components/BulkImport/BulkImport';
import CircularProgress from '@material-ui/core/CircularProgress';
import ModelPortfolioAllocationOptions from 'components/CipManagementv2/ModelPortfolioAllocationOptions';
import { useParams } from 'react-router-dom';
import {
  getCashFundIndex,
  checkFundExists
} from 'components/Forms/ExistingPolicies/ManagePolicies/Funds/utils';
import Store from 'store/store';
import Checkbox from 'components/Checkbox/Checkbox';
import { Button } from 'components/Base';

const { connect, selectors } = Store;

const useStyles = makeStyles((theme: Theme) => ({
  emptyRow: {
    padding: 0
  },
  footer: {
    fontWeight: 500,
    color: 'black',
    borderColor: '#fff'
  },
  title: {
    fontSize: '1rem',
    lineHeight: '1.3rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    marginRight: '1rem'
  },
  mainTitle: {
    fontSize: '1.1rem',
    lineHeight: '1.3rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    margin: '1rem 0 0 0 '
  },
  empty: {
    borderColor: '#fff'
  },
  searchBox: {
    width: '30%'
  },
  headerWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'end',
    width: '100%',
    height: '6rem',
    position: 'relative',
    gap: '1rem'
  },
  table: {
    backgroundColor: '#fff'
  },
  wrapCash: {
    margin: '1rem 1.2rem 2rem',
    userSelect: 'none',
    cursor: 'pointer'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3, 0)
  },
  paper: {
    width: '100%',
    flex: 1,
    margin: 0,
    '& > div': {
      height: 'unset'
    }
  },
  searchPorfolioWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '40%',
    marginRight: '1.5rem'
  },
  BulkImportWrapper: {
    display: 'flex',
    alignItems: 'center',
    width: '10%',
    marginRight: '1.5rem'
  },
  headerBoxWrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    justifyContent: 'center',
    paddingLeft: '1rem',
    paddingRight: '1rem'
  },
  note: {
    marginTop: 'auto',
    marginBottom: '0.5rem',
    marginLeft: '1rem'
  }
}));

interface FundsProps {
  fields: any;
  isWrap: boolean;
  wrapRetained?: string;
  cipFunds: Array<FundType>;
  cipTemplate: Array<FundType>;
  performedAction: null | number;
  cashFund: any;
  caseInfo: any;
}

const Funds = ({
  fields,
  isWrap,
  wrapRetained,
  cipFunds,
  cipTemplate,
  performedAction,
  cashFund,
  caseInfo
}: FundsProps) => {
  const isEvalue = caseInfo.riskProfiler === 'EValue';
  const riskHeaders = isEvalue
    ? ['Risk']
    : ['Growth assets', 'Risk comfort score'];
  const [isWrapChecked, setIsWrapCash] = useState(isWrap);
  const [openBulkImport, setOpenBulkImport] = useState(false);
  const { portfolio } = useParams() as any;
  const classes = useStyles();
  const existingFields = fields.getAll();
  const isRetaintActionApplied = performedAction === 1;
  const hasValues = existingFields && existingFields.length > 0;
  const existingWeightingSum = sumExistingWeighting(existingFields);
  const recommendedWeightingSum = sumRecommendedWeighting(existingFields);

  useEffect(() => {
    existingFields.forEach(async (fund: any, index: number) => {
      if ((isEvalue && !fund.fund) || 'risk' in fund || fund.gettingRisk)
        return;
      fields.splice(index, 1, { ...fund, gettingRisk: true });

      const method = isEvalue
        ? api.getFundRiskRating({ fundId: fund.fund.id, caseId: portfolio })
        : api.getMorningstarRiskRating({
            fundId: fund.fund?.id,
            modelPortfolioId: fund.model_portfolio?.id
          });

      method
        .then(({ data }) => {
          const risk = data == 0 ? '-' : data;
          fields.splice(index, 1, { ...fund, risk: risk, gettingRisk: false });
        })
        .catch(() => {
          fields.splice(index, 1, { ...fund, risk: '-', gettingRisk: false });
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingFields]);

  // INFO hack for rounding issue, aggred that for now we will apply
  // fix, and treat 99.99 and 100.01 values as correct for retained policies
  const correctValues = isRetaintActionApplied ? [99.9, 100, 100.01] : [100];
  const isRecommendedWeigtingSettedCorectly = correctValues.includes(
    recommendedWeightingSum
  );

  const addNewFund = (fundName: any) => {
    try {
      if (!fundName) return;
      if ('fund' in fundName && !fundName.model_portfolio) {
        fundName = fundName.fund;
      }

      const isAlreadyAdded = checkFundExists(existingFields, fundName);

      if (isAlreadyAdded)
        return Notify.warn(
          'Fund already added',
          'Duplicated funds are not allowed'
        );

      const indexToInsert = isWrapChecked ? fields.length - 1 : fields.length;
      const isModelPortfolio = !!fundName.model_portfolio;

      if (isModelPortfolio) {
        fields.insert(indexToInsert, {
          ...fundName,
          existing_weighting: 0,
          allocation: fundName.allocation
            ? formatPercent(Number(fundName.allocation) * 100)
            : formatPercent(0),
          fund_charge_overrides: fundName.model_portfolio.fund_allocations.map(
            (allocation: any) => {
              return { fund: allocation.fund, ocf: null, trading_costs: null };
            }
          )
        });
      } else {
        const method = isEvalue
          ? api.getFundRiskRating({ fundId: fundName.id, caseId: portfolio })
          : api.getMorningstarRiskRating({ fundId: fundName.id });

        method.then(response => {
          fields.insert(indexToInsert, {
            fund: { ...fundName },
            existing_weighting: 0,
            allocation: fundName.allocation
              ? formatPercent(Number(fundName.allocation) * 100)
              : formatPercent(0),
            risk: response.data,
            fund_charge_overrides: [
              { fund: fundName, ocf: null, trading_costs: null }
            ]
          });
        });
      }
    } catch (err) {
      Notify.error(err as string);
    }
  };

  const removeFund = (fundIndex: number) => {
    const wrapCashIndex = fields.length - 1;

    if (isWrapChecked && wrapCashIndex === fundIndex) {
      fields.remove(fundIndex);
      return setIsWrapCash(false);
    }
    fields.remove(fundIndex);
  };

  const toggleWrapCash = () => {
    if (isWrapChecked) {
      removeFund(fields.length - 1);
      return setIsWrapCash(false);
    }

    const wrapCash = {
      fund: {
        name: 'Wrap Cash',
        active: true
      },
      existing_weighting: !wrapRetained ? 0 : Number(wrapRetained) * 100,
      allocation: formatPercent(0)
    };
    fields.insert(fields.length, wrapCash);
    return setIsWrapCash(true);
  };

  const toggleCipFund = (fund: any) => {
    const fundIndex = existingFields.findIndex((addedFund: any) => {
      const isModelPortfolioInExisting = !!addedFund.model_portfolio;

      const idToCheckExisting = isModelPortfolioInExisting
        ? addedFund.model_portfolio.id
        : addedFund.fund.id;

      const idToCheckFund = fund.model_portfolio
        ? fund.model_portfolio.id
        : fund.id;

      return idToCheckExisting === idToCheckFund;
    });

    const isAlreadyAdded = fundIndex !== -1;

    if (isAlreadyAdded) {
      return removeFund(fundIndex);
    }

    addNewFund(fund);
  };

  const addModelPortfolioDefinition = (modelPortfolio: ModelPortfolioType) => {
    api
      .getModelPortfolio({ modelPortfolioId: modelPortfolio.value })
      .then(({ data }: any) => {
        const {
          id,
          name,
          ocf,
          date,
          trading_costs,
          total_fund_charge,
          one_y_performace,
          three_y_performace,
          five_y_performace,
          fund_allocations,
          type
        } = data;
        const indexToInsert = isWrapChecked ? fields.length - 1 : fields.length;

        const fakeFund = {
          fund: null,
          model_portfolio: {
            active: !!fund_allocations.find((fund: any) => fund.fund?.active),
            id,
            date: date,
            name: name,
            one_y_performace,
            three_y_performace,
            five_y_performace,
            fund_allocations: fund_allocations,
            ocf,
            trading_costs,
            total_fund_charge,
            type
          },
          allocation: formatPercent(0),
          existing_weighting: 0,
          fund_value: formatCurrency(0),
          fund_charge_overrides: fund_allocations.map((allocation: any) => {
            return { fund: allocation.fund, ocf: null, trading_costs: null };
          })
        };
        fields.insert(indexToInsert, fakeFund);
      })
      .catch((err: any) => {
        errHelper(err);
      });
  };

  const addModelPortfolio = (modelPortfolio: ModelPortfolioType) => {
    if (!modelPortfolio) return;
    if (modelPortfolio.model_portfolio_selection) {
      api
        .getLatestDefinition({ id: modelPortfolio.value })
        .then(({ data }: any) => {
          addModelPortfolioDefinition({ ...data, value: data.id });
        });

      return;
    }

    addModelPortfolioDefinition({
      ...modelPortfolio,
      value: modelPortfolio.mp_id || modelPortfolio.value
    });
  };

  const bulkImportToPolicy = (funds: any) => {
    const indexToInsert = isWrapChecked ? fields.length - 1 : fields.length;
    funds.forEach((fund: any) => {
      fields.insert(indexToInsert, {
        ...fund,
        fund_charge_overrides: [
          { fund: fund.fund, ocf: null, trading_costs: null }
        ]
      });
    });
  };

  const hasCashFund = !!checkFundExists(existingFields, cashFund);

  const toggleCashFund = (fund: any) => {
    if (hasCashFund) {
      return removeFund(getCashFundIndex(fields));
    }

    return addNewFund(fund);
  };

  const [checkedState, setCheckedState] = useState<{ [key: number]: boolean }>(
    {}
  );
  const [allChecked, setAllChecked] = useState(false);

  const handleCheckboxChange = (index: number) => {
    setCheckedState(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleAllCheckboxChange = () => {
    const newCheckedState = existingFields.reduce(
      (acc: any, _: any, index: number) => {
        acc[index] = !allChecked;
        return acc;
      },
      {}
    );
    setCheckedState(newCheckedState);
    setAllChecked(!allChecked);
  };

  const handleDeleteSelectedFunds = () => {
    const selectedIndices = Object.entries(checkedState)
      .filter(([, isChecked]) => isChecked)
      .map(([index]) => parseInt(index));

    // Remove from last to first to keep indices consistent
    selectedIndices
      .sort((a, b) => b - a)
      .forEach(index => {
        removeFund(index);
      });

    setCheckedState({});
    setAllChecked(false);
  };

  return (
    <div style={{ paddingBottom: '88px' }}>
      <Grid container spacing={2}>
        <Grid
          item
          xs={12}
          className={classes.header}
          style={{ flexDirection: 'column', marginTop: '28px' }}
        >
          {/* // Add funds */}
          <BoxWrapper title="" className={classes.paper}>
            {!isRetaintActionApplied && (
              <div className={classes.headerBoxWrapper}>
                <Typography className={classes.mainTitle} variant="h3">
                  Recommended holdings
                </Typography>

                <div className={classes.headerWrapper}>
                  <ModelPortfolioAllocationOptions
                    canAddFundManually={false}
                    canAddCustomPortfolios
                    inline
                    addInvestment={(imports: any[], type = 'f') => {
                      imports.forEach((item: any) => {
                        if (type === 'f') {
                          addNewFund(item);
                        } else {
                          addModelPortfolio(item);
                        }
                      });
                    }}
                  />
                </div>
                <div
                  style={{
                    marginLeft: 'auto',
                    display: 'flex',
                    gap: '1rem',
                    paddingBottom: '2rem',
                    paddingRight: '2rem'
                  }}
                >
                  <Fields.CheckboxCustom
                    isChecked={hasCashFund}
                    label="Pound sterling"
                    onChange={() => toggleCashFund(cashFund)}
                  />
                  <Fields.WrapCash
                    toggleWrapCash={toggleWrapCash}
                    wrapChecked={isWrapChecked}
                    tooltip="Any amounts assigned to wrap cash are excluded from all risk assessments and asset allocation"
                  />
                </div>
              </div>
            )}
            {hasValues && (
              <>
                <div
                  style={{
                    marginLeft: 'auto',
                    padding: '1rem'
                  }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDeleteSelectedFunds}
                    disabled={!Object.values(checkedState).some(v => v)}
                  >
                    Remove Selected
                  </Button>
                </div>
                <Table style={{ backgroundColor: '#fff' }}>
                  <TableComponents.TableHead
                    cells={[
                      <Checkbox
                        key="select-all-checkbox"
                        onClick={handleAllCheckboxChange}
                        isChecked={allChecked}
                      />,
                      'Fund',
                      'Sector',
                      ...riskHeaders,
                      'OCF',
                      'Transaction cost',
                      'Total Fund Charge',
                      'Existing weighting %',
                      'Recommended weighting %',
                      ''
                    ]}
                  />
                  <TableComponents.TableBody>
                    {fields.map((fund: any, index: number) => {
                      const fundValues = fields.get(index);

                      const isModelPortfolio = !!fundValues.model_portfolio;
                      const { existing_weighting } = fundValues;
                      const { sector_name, name, ocf, trading_costs } =
                        isModelPortfolio
                          ? fundValues.model_portfolio
                          : fundValues.fund;

                      const hasMPInactiveFund = isModelPortfolio
                        ? fundValues.model_portfolio.fund_allocations.some(
                            (fundDetails: any) => {
                              return (
                                fundDetails.fund && !fundDetails.fund.active
                              );
                            }
                          )
                        : false;

                      const isFundActive = isModelPortfolio
                        ? !hasMPInactiveFund
                        : fundValues.fund.active;

                      return (
                        <Fund
                          fund={fund}
                          key={index}
                          sectorName={sector_name}
                          ocf={ocf}
                          tradingCosts={trading_costs}
                          existing_weighting={existing_weighting}
                          removeFund={() => removeFund(index)}
                          modelPortfolio={fundValues.model_portfolio}
                          overrides={fundValues.fund_charge_overrides}
                          name={name}
                          isReadOnly={isRetaintActionApplied}
                          isFundActive={isFundActive}
                          isEvalue={isEvalue}
                          risk={
                            (isEvalue && fundValues.fund) || !isEvalue
                              ? fundValues.risk || (
                                  <CircularProgress
                                    style={{ flex: 'none' }}
                                    color="inherit"
                                    size={12}
                                  />
                                )
                              : '-'
                          }
                          isChecked={!!checkedState[index]}
                          onToggleCheck={() => handleCheckboxChange(index)}
                        />
                      );
                    })}
                  </TableComponents.TableBody>
                  <TableComponents.TableFooter>
                    <TableRow>
                      <TableComponents.TableCell
                        className={classes.empty}
                      ></TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.empty}
                      ></TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.footer}
                        width="220px"
                      >
                        Overall
                      </TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.empty}
                      ></TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.empty}
                      ></TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.empty}
                      ></TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.footer}
                        width="120px"
                      >
                        {formatPercent(existingWeightingSum)}
                      </TableComponents.TableCell>
                      <TableComponents.TableCell
                        className={classes.footer}
                        width="120px"
                      >
                        <span
                          style={{
                            color:
                              isRecommendedWeigtingSettedCorectly || !hasValues
                                ? 'inherit'
                                : 'red'
                          }}
                        >
                          {formatPercent(
                            isRetaintActionApplied
                              ? 100
                              : recommendedWeightingSum || 0,
                            true
                          )}
                        </span>
                      </TableComponents.TableCell>
                      <TableComponents.TableCell
                        width="50px"
                        className={classes.footer}
                      ></TableComponents.TableCell>
                    </TableRow>
                  </TableComponents.TableFooter>
                </Table>
              </>
            )}
            {isRetaintActionApplied && (
              <span className={classes.note}>
                Fund switches are not permitted as the selected recommendation
                is 'Retain - no changes'
              </span>
            )}
          </BoxWrapper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {!isRetaintActionApplied && (
          <Grid item xs={isEvalue ? 9 : 12}>
            <CipSelection
              cipTemplate={cipTemplate}
              toggleCipFund={toggleCipFund}
              fundsAdded={existingFields}
              addModelPortfolio={addModelPortfolio}
              isEvalue={isEvalue}
            />
          </Grid>
        )}
        {isEvalue && (
          <Grid item xs={3}>
            <div style={{ marginTop: '-12px' }}>
              <AssetAllocations />
            </div>
          </Grid>
        )}
      </Grid>

      {!isRetaintActionApplied && (
        <Grid item xs={12}>
          <CipTable
            cipFunds={cipFunds}
            toggleCipFund={toggleCipFund}
            fundsAdded={existingFields}
          />
        </Grid>
      )}
      <BulkImport
        openBulkImport={openBulkImport}
        setOpenBulkImport={setOpenBulkImport}
        importToPolicy={bulkImportToPolicy}
      />
    </div>
  );
};

export default connect((state: any) => ({
  cashFund: selectors.metaSelectors.cashFund(state),
  caseInfo: selectors.caseSelectors.case(state)
}))(Funds);
